import React from "react";
import {
    Box,
    Typography,
    Grid,
    Container,
    useTheme,
} from "@mui/material";

const steps = [
    {
        title: "Mission",
        subtitle: "Smarter saving. Better earning. Future-ready",
        image: "https://ik.imagekit.io/arthgyan/Arthgyan_Web/mission?updatedAt=1756998051532",
        number: "01",
    },
    {
        title: "Vision",
        subtitle: "Your family’s money. One smart app",
        image: "https://ik.imagekit.io/arthgyan/Arthgyan_Web/vision?updatedAt=1756998051643",
        number: "02",
    },
    {
        title: "Value",
        subtitle: "Grow your wealth smartly",
        image: "https://ik.imagekit.io/arthgyan/Arthgyan_Web/value?updatedAt=1756998051635",
        number: "03",
    },
];

const MissionCard = ({ title, subtitle, image, number }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                minHeight: { xs: 170, md: 190 },
                p: { xs: 2.5, md: 3 },
                borderRadius: "16px",
                backgroundColor: "#ffffff",
                border: "1px solid #e5edf6",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                transition: "all 0.3s ease",
                boxShadow: "0 4px 18px rgba(30, 70, 120, 0.045)",

                "&:hover": {
                    transform: "translateY(-5px)",
                    borderColor: theme.palette.primary.main,
                    boxShadow: "0 12px 28px rgba(0, 123, 255, 0.10)",
                },

                "&:hover .mission-icon": {
                    backgroundColor: "#e5f1ff",
                    transform: "scale(1.04)",
                },

                "&:hover .mission-line": {
                    width: "65px",
                },
            }}
        >
            {/* Top row */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2.5,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            backgroundColor: "#00a8a8",
                        }}
                    />

                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "1.4px",
                            color: "#708197",
                            textTransform: "uppercase",
                        }}
                    >
                        ARTHGYAN
                    </Typography>
                </Box>

                <Typography
                    sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#a6b8ca",
                        letterSpacing: "1px",
                    }}
                >
                    {number}
                </Typography>
            </Box>

            {/* Main content */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 2, md: 2.5 },
                    flexGrow: 1,
                }}
            >
                {/* Icon block */}
                <Box
                    className="mission-icon"
                    sx={{
                        flexShrink: 0,
                        width: { xs: 65, md: 72 },
                        height: { xs: 65, md: 72 },
                        borderRadius: "18px",
                        backgroundColor: "#f0f7ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #e5effb",
                        transition: "all 0.3s ease",
                    }}
                >
                    <Box
                        component="img"
                        src={image}
                        alt={title}
                        sx={{
                            width: 43,
                            height: 43,
                            objectFit: "contain",
                        }}
                    />
                </Box>

                {/* Text block */}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: 18,
                            color: theme.palette.text.primary,
                            mb: 0.8,
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: 13.5,
                            lineHeight: 1.7,
                            color: "#526174",
                        }}
                    >
                        {subtitle}
                    </Typography>
                </Box>
            </Box>

            {/* Bottom accent */}
            <Box
                sx={{
                    mt: 2.5,
                    height: 3,
                    width: "100%",
                    borderRadius: 5,
                    backgroundColor: "#edf3f9",
                    overflow: "hidden",
                }}
            >
                <Box
                    className="mission-line"
                    sx={{
                        height: "100%",
                        width: "35px",
                        borderRadius: 5,
                        background:
                            "linear-gradient(90deg, #007bff, #00a8a8)",
                        transition: "width 0.3s ease",
                    }}
                />
            </Box>
        </Box>
    );
};

const Mission = () => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                py: { xs: 4, md: 2 },
                px: { xs: 2, md: 0 },
            }}
        >
            <Typography
                sx={{
                    fontSize: { xs: "20px", sm: "28px", md: "40px" },
                    fontWeight: 600,
                    textAlign: "center",
                    lineHeight: 1.1,
                    mb: 3,
                    animation: "fadeUp 0.9s ease-out both",
                    color: theme.palette.text.primary,
                    position: "relative",
                    display: "inline-block",
                    width: "100%",
                }}
            >
                Our{" "}
                <Box
                    component="span"
                    sx={{
                        fontWeight: 900,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.text.primary})`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        position: "relative",
                        display: "inline-block",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: "-4px",
                            left: 0,
                            width: "100%",
                            height: "2px",
                            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, transparent 100%)`,
                        },
                    }}
                >
                    Mission
                </Box>
            </Typography>

            <Container maxWidth="lg">
                <Grid
                    container
                    spacing={{ xs: 2, md: 2.5 }}
                    justifyContent="center"
                    alignItems="stretch"
                >
                    {steps.map((step) => (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={step.number}
                            sx={{ display: "flex" }}
                        >
                            <MissionCard {...step} />
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default Mission;
